/**
 * Expands the header mega drawer on nav hover / focus (desktop only).
 */
class HeaderMegaMenu {
  constructor() {
    this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    this.header = document.querySelector('.header--has-mega-drawer');
    this.drawer = document.querySelector('.header__mega-drawer');
    this.navList = this.header?.querySelector('.header__inline-menu .js-list-menu');

    if (!this.header || !this.drawer || !this.navList) return;

    this.panels = [
      ...this.drawer.querySelectorAll('.header__mega-panel--desktop'),
    ];
    this.panelMap = new Map(
      this.panels.map((panel) => [panel.dataset.menuHandle, panel]),
    );
    this.activeHandle = null;
    this.activePanel = null;
    this.closeDelay = null;
    this.navRaf = null;

    this.onNavOver = this.onNavOver.bind(this);
    this.handleNavOver = this.handleNavOver.bind(this);
    this.onHeaderLeave = this.onHeaderLeave.bind(this);
    this.onDrawerEnter = this.onDrawerEnter.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);

    this.desktopMediaQuery.addEventListener('change', this.onDesktopChange);

    this.navList.addEventListener('mouseover', this.onNavOver);
    this.header.addEventListener('mouseleave', this.onHeaderLeave);
    this.drawer.addEventListener('mouseenter', this.onDrawerEnter);

    this.header.classList.add('header--mega-initialized');

    if (!this.isDesktop()) {
      this.resetMegaState();
    } else {
      this.close();
    }
  }

  isDesktop() {
    return this.desktopMediaQuery.matches;
  }

  onDesktopChange(event) {
    if (!event.matches) {
      this.resetMegaState();
    }
  }

  resetMegaState() {
    window.clearTimeout(this.closeDelay);
    window.clearTimeout(this.transitionTimeout);

    this.activeHandle = null;
    this.activePanel = null;

    if (!this.header) return;

    this.setTransitioning(false);
    this.header.classList.remove('header--mega-open');
    delete this.header.dataset.activeMegaMenu;
    document.body.classList.remove('has-header-mega-open');

    this.panels.forEach((panel) => {
      panel.classList.remove('header__mega-panel--active');
      panel.setAttribute('aria-hidden', 'true');
    });
  }

  onNavOver(event) {
    if (!this.isDesktop()) return;

    this.pendingNavEvent = event;

    if (this.navRaf) return;

    this.navRaf = window.requestAnimationFrame(() => {
      this.navRaf = null;
      this.handleNavOver(this.pendingNavEvent);
    });
  }

  handleNavOver(event) {
    if (!this.isDesktop()) return;

    if (event.target.closest('.header__mega-drawer')) return;

    const plainItem = event.target.closest('.header__nav-item--link');
    if (plainItem) {
      window.clearTimeout(this.closeDelay);
      this.close();
      return;
    }

    const megaItem = event.target.closest('.header__nav-item--mega');
    if (!megaItem) return;

    const handle = megaItem.querySelector('[data-menu-handle]')?.dataset.menuHandle;
    if (!handle) return;

    window.clearTimeout(this.closeDelay);
    this.open(handle);
  }

  onDrawerEnter() {
    if (!this.isDesktop()) return;

    window.clearTimeout(this.closeDelay);
  }

  onHeaderLeave(event) {
    if (!this.isDesktop()) return;

    const related = event.relatedTarget;

    if (related && this.header.contains(related)) return;

    this.scheduleClose();
  }

  scheduleClose() {
    window.clearTimeout(this.closeDelay);
    this.closeDelay = window.setTimeout(() => this.close(), 120);
  }

  setTransitioning(isActive) {
    window.headerMegaMenuTransitioning = isActive;
    this.header.classList.toggle('header--mega-transitioning', isActive);
    document.dispatchEvent(
      new CustomEvent('headerMegaMenuTransition', {
        detail: { active: isActive },
      }),
    );
  }

  open(handle) {
    if (!this.isDesktop()) return;

    if (
      this.activeHandle === handle &&
      this.header.classList.contains('header--mega-open')
    ) {
      return;
    }

    const nextPanel = this.panelMap.get(handle);
    if (!nextPanel) return;

    const wasOpen = this.header.classList.contains('header--mega-open');
    const isSwap = wasOpen && this.activeHandle !== handle;

    this.setTransitioning(true);

    if (this.activePanel && this.activePanel !== nextPanel) {
      this.activePanel.classList.remove('header__mega-panel--active');
      this.activePanel.setAttribute('aria-hidden', 'true');
    }

    this.activeHandle = handle;
    this.activePanel = nextPanel;
    this.header.classList.add('header--mega-open');
    this.header.dataset.activeMegaMenu = handle;
    document.body.classList.add('has-header-mega-open');

    nextPanel.classList.add('header__mega-panel--active');
    nextPanel.setAttribute('aria-hidden', 'false');

    window.clearTimeout(this.transitionTimeout);

    this.queueMeasure();

    if (isSwap) {
      this.setTransitioning(false);
    } else {
      this.transitionTimeout = window.setTimeout(
        () => this.setTransitioning(false),
        wasOpen ? 0 : 50,
      );
    }
  }

  close() {
    if (!this.isDesktop()) {
      this.resetMegaState();
      return;
    }

    if (!this.header.classList.contains('header--mega-open')) return;

    this.setTransitioning(true);
    this.activeHandle = null;
    this.activePanel = null;
    this.header.classList.remove('header--mega-open');
    delete this.header.dataset.activeMegaMenu;
    document.body.classList.remove('has-header-mega-open');

    this.panels.forEach((panel) => {
      panel.classList.remove('header__mega-panel--active');
      panel.setAttribute('aria-hidden', 'true');
    });

    window.clearTimeout(this.transitionTimeout);
    this.transitionTimeout = window.setTimeout(() => {
      this.setTransitioning(false);
      this.queueMeasure();
    }, 50);
  }

  queueMeasure() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('headerMegaMenuMeasure'));
      });
    });
  }
}

function initHeaderMegaMenu() {
  new HeaderMegaMenu();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderMegaMenu);
} else {
  initHeaderMegaMenu();
}
